import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, X } from "lucide-react";
import { menuHighlights, menuLookupKeys } from "../data/mock";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "mm_special_dismissed_id";

/**
 * Renders a slim "Today's Special" strip at the very top of every page.
 * - Fetches the current special from `/api/menu/special`.
 * - Merges DB values on top of `mock.js` so name/image/description resolve
 *   even for the historic static dishes (ids 1-32).
 * - Users can dismiss the banner for that specific special id (via X);
 *   preference lives in localStorage so it comes back when a new dish is
 *   set as today's special.
 * - Hides completely on /admin, /staff, /kitchen and print pages so it
 *   doesn't clutter the operational surfaces.
 */
export default function TodaysSpecialBanner({ variant = "strip" }) {
  const [special, setSpecial] = useState(null);
  const [dismissedId, setDismissedId] = useState(() => {
    try { return typeof localStorage !== "undefined" ? localStorage.getItem(DISMISS_KEY) : null; }
    catch { return null; }
  });
  const { add, setOpen } = useCart();

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/menu/special`).then(({ data }) => {
      if (cancelled) return;
      const raw = data?.item;
      if (!raw) { setSpecial(null); return; }
      // Merge with static mock.js so we get name/image/desc for ids 1-32
      const staticItem = menuHighlights.find((m) => m.id === raw.id);
      const merged = {
        id: raw.id,
        name: raw.name || staticItem?.name || "Today's special",
        description: raw.description || staticItem?.description || "",
        image: raw.image || staticItem?.image || null,
        price: raw.price != null ? Number(raw.price) : parseFloat((staticItem?.price || "0").replace(/[^\d.]/g, "")) || 0,
        lookup_key: raw.lookup_key || menuLookupKeys[raw.id] || `mamamina_${raw.id}`,
        tag: raw.tag || staticItem?.tag || "Signature",
      };
      setSpecial(merged);
    }).catch(() => setSpecial(null));
    return () => { cancelled = true; };
  }, []);

  // Hide on operational routes — banner is only for the customer surface.
  if (typeof window !== "undefined") {
    const path = window.location.pathname;
    if (path.startsWith("/admin") || path.startsWith("/staff") || path.startsWith("/kitchen") || path.startsWith("/print")) {
      return null;
    }
  }

  if (!special) return null;
  if (String(dismissedId) === String(special.id)) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(special.id)); } catch { /* ignore */ }
    setDismissedId(String(special.id));
  };

  const addToCart = () => {
    add({
      lookup_key: special.lookup_key,
      name: special.name,
      price: special.price,
      image: special.image,
    });
    setOpen(true);
  };

  if (variant === "hero") {
    return <HeroCard special={special} onAdd={addToCart} />;
  }

  return (
    <div
      data-testid="todays-special-banner"
      className="relative w-full bg-[var(--mm-accent)] text-[#F6F5F2]"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-2.5 flex items-center gap-3 md:gap-4">
        <Sparkles size={13} className="shrink-0" />
        <div className="text-[0.62rem] md:text-xs tracking-[0.22em] uppercase font-semibold shrink-0 hidden md:inline">
          Today&apos;s Special
        </div>
        <div className="flex-1 min-w-0 text-xs md:text-sm flex items-center gap-2 truncate">
          <span className="font-display font-semibold text-[#F6F5F2]">{special.name}</span>
          <span className="opacity-70 hidden md:inline">·</span>
          <span className="tabular-nums text-[#F6F5F2]">${special.price.toFixed(special.price % 1 === 0 ? 0 : 2)}</span>
        </div>
        <button
          onClick={addToCart}
          data-testid="todays-special-add"
          className="text-[0.62rem] md:text-[0.66rem] tracking-[0.22em] uppercase font-semibold underline underline-offset-4 hover:no-underline shrink-0"
        >
          Add to cart
        </button>
        <button
          onClick={dismiss}
          data-testid="todays-special-dismiss"
          aria-label="Dismiss today's special"
          className="ml-1 md:ml-2 opacity-60 hover:opacity-100 transition-opacity shrink-0"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

function HeroCard({ special, onAdd }) {
  return (
    <div
      data-testid="todays-special-hero"
      className="relative overflow-hidden border border-[var(--mm-accent)] bg-[var(--mm-bg-alt)] fade-in"
    >
      <div className="grid md:grid-cols-5">
        {special.image && (
          <div className="md:col-span-2 aspect-[4/3] md:aspect-auto overflow-hidden">
            <img
              src={special.image}
              alt={special.name}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className={`p-6 md:p-10 flex flex-col justify-between ${special.image ? "md:col-span-3" : "md:col-span-5"}`}>
          <div>
            <div className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 border border-[var(--mm-accent)] text-[var(--mm-accent)] text-[0.6rem] tracking-[0.24em] uppercase">
              <Sparkles size={11} /> Today&apos;s Special
            </div>
            <h3 className="font-display italic text-3xl md:text-5xl text-[var(--mm-cream)] leading-[1.05] mb-3">
              {special.name}
            </h3>
            {special.description && (
              <p className="text-[var(--mm-cream-muted)] font-light leading-relaxed max-w-lg">
                {special.description}
              </p>
            )}
          </div>
          <div className="mt-6 flex items-end justify-between gap-4">
            <div className="font-display italic text-3xl md:text-4xl text-[var(--mm-cream)] tabular-nums">
              ${special.price.toFixed(special.price % 1 === 0 ? 0 : 2)}
            </div>
            <button onClick={onAdd} data-testid="todays-special-hero-add" className="btn-primary">
              Add to cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
